import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ZBucksDashboard — the human user's own real zbucks paper-trading ledger.
// Was previously calling /omega/v2/zbucks/dashboard|account|forecast|cycle,
// none of which exist on the backend (confirmed via grep across server.py —
// zero matches), against a hardcoded USER_ID="default" that was never the
// real logged-in user either. The component silently hung on "Loading
// ZBucks..." forever for every real user.
//
// Fixed to read the real ledger instead: agent_zbucks_service tracks the
// human's own trades in agent_profiles (agent_key="user") exactly like it
// does for the 23 AI trading agents — GET /zbucks/user/summary (added
// alongside this fix) and the already-real GET /zbucks/user/positions.
// The old "Readiness Score", "Autopilot Forecast", and "Manual Learning
// Cycle" sections had no real backend counterpart at all, so rather than
// inventing formulas to fill them, they're gone — this shows only what's
// real: balance, P&L, win rate, live open positions, recent closed trades.
export default function ZBucksDashboard() {
  const [summary, setSummary] = useState(null);
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const [s, p] = await Promise.all([
        axios.get(`${API}/zbucks/user/summary`),
        axios.get(`${API}/zbucks/user/positions`),
      ]);
      setSummary(s.data);
      setPositions(p.data?.positions || []);
      setError("");
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Couldn't load your zBucks ledger.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); const iv = setInterval(load, 15000); return () => clearInterval(iv); }, [load]);

  if (loading) return <div className="card"><div style={{ fontSize: 11, color: "var(--muted)" }}>Loading your zBucks ledger…</div></div>;
  if (error) return <div className="card"><div style={{ fontSize: 11, color: "var(--red)" }}>{error}</div></div>;

  const s = summary || {};
  const pnlPositive = (s.totalPnl || 0) >= 0;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

      {/* Account Header — real balance, real all-time P&L */}
      <div className="card">
        <div className="row" style={{ marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text)" }}>Your zBucks Account</div>
            <div style={{ fontSize: 10, color: "var(--muted)" }}>Paper trading — same ledger real orders can be funded from instead of USD</div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: "var(--green)" }}>Ⓩ{(s.balance ?? 0).toFixed(2)}</div>
            <div style={{ fontSize: 9, color: "var(--dim)" }}>Started: Ⓩ{(s.startingBalance ?? 500).toFixed(0)}</div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 12, fontSize: 10, color: "var(--muted)", flexWrap: "wrap" }}>
          <span>Total PnL: <strong style={{ color: pnlPositive ? "var(--green)" : "var(--red)" }}>{pnlPositive ? "+" : ""}Ⓩ{(s.totalPnl ?? 0).toFixed(2)}</strong></span>
          <span>Win Rate: <strong style={{ color: "var(--text)" }}>{s.winRate != null ? `${s.winRate}%` : "— (no closed trades yet)"}</strong></span>
          <span>Wins: <strong style={{ color: "var(--green)" }}>{s.wins ?? 0}</strong></span>
          <span>Losses: <strong style={{ color: "var(--red)" }}>{s.losses ?? 0}</strong></span>
        </div>
      </div>

      {/* Open Positions — real, live-priced */}
      <div className="card">
        <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text)", marginBottom: 10 }}>Open zBucks Positions {positions.length > 0 && `(${positions.length})`}</div>
        {positions.length === 0 ? (
          <div style={{ fontSize: 10, color: "var(--dim)" }}>No open zBucks positions. Trade with zBucks from the order confirm screen to see them here.</div>
        ) : positions.map((p) => (
          <div key={p.lockId} style={{ padding: "8px 10px", borderRadius: 6, background: "var(--layer1)", border: "1px solid var(--border)", marginBottom: 6 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 6 }}>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: "var(--text)" }}>{p.ticker}</span>
                {p.optionType && <span style={{ fontSize: 10, color: "var(--muted)" }}>${p.strike} {p.optionType?.toUpperCase()}</span>}
                <span style={{ fontSize: 10, color: "var(--muted)" }}>x{p.quantity} @ Ⓩ{p.avgCost}</span>
              </div>
              <span style={{ fontSize: 11, fontWeight: 700, color: p.gainPct >= 0 ? "var(--green)" : "var(--red)" }}>
                {p.gainPct >= 0 ? "+" : ""}{p.gainPct}% ({p.gainValue >= 0 ? "+" : ""}Ⓩ{p.gainValue?.toFixed(2)})
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Closed Trades — real, from the same ledger settle_zbucks() writes to */}
      {(s.closedTrades || []).length > 0 && (
        <div className="card">
          <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text)", marginBottom: 10 }}>Recent Closed Trades</div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 10 }}>
            <thead>
              <tr style={{ color: "var(--muted)", borderBottom: "1px solid var(--border)" }}>
                <th style={{ textAlign: "left", padding: "4px 6px" }}>Symbol</th>
                <th style={{ textAlign: "right", padding: "4px 6px" }}>Cost</th>
                <th style={{ textAlign: "right", padding: "4px 6px" }}>PnL</th>
                <th style={{ textAlign: "right", padding: "4px 6px" }}>Closed</th>
              </tr>
            </thead>
            <tbody>
              {s.closedTrades.map((t) => (
                <tr key={t.lock_id} style={{ borderBottom: "1px solid var(--border)" }}>
                  <td style={{ padding: "4px 6px", fontWeight: 600, color: "var(--text)" }}>{t.ticker}</td>
                  <td style={{ padding: "4px 6px", textAlign: "right", color: "var(--muted)" }}>Ⓩ{t.cost?.toFixed(2)}</td>
                  <td style={{ padding: "4px 6px", textAlign: "right", fontWeight: 700, color: (t.pnl ?? 0) >= 0 ? "var(--green)" : "var(--red)" }}>
                    {(t.pnl ?? 0) >= 0 ? "+" : ""}Ⓩ{(t.pnl ?? 0).toFixed(2)}
                  </td>
                  <td style={{ padding: "4px 6px", textAlign: "right", color: "var(--dim)" }}>{t.closed_at ? new Date(t.closed_at).toLocaleDateString() : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
